import { create } from "zustand";

import type {
  AuthActions,
  AuthEmitter,
  AuthError,
  AuthMode,
  AuthState,
  AuthUiStatus,
  FrontendUser,
} from "./types";
import { createAuthEmitter, createUseAuthEvent } from "./events";

interface SuspenseState {
  promise: Promise<FrontendUser> | null;
  resolve: ((user: FrontendUser) => void) | null;
}

interface AuthStoreState extends AuthState {
  emitter: AuthEmitter;
  previousUserId: string | null;
  signOutFn: (() => Promise<void>) | null;
  suspense: SuspenseState;
}

type AuthStore = AuthStoreState &
  AuthActions & {
    emitSignedOut: () => void;
    emitUserChanged: (previousId: string, newId: string) => void;
    emitTokenRefreshed: (token: string) => void;
    reset: () => void;
    registerSignOut: (fn: () => Promise<void>) => void;
    signOut: () => Promise<void>;
    getSuspensePromise: () => Promise<FrontendUser>;
    resolveSuspense: (user: FrontendUser) => void;
  };

const initialState: AuthStoreState = {
  user: null,
  token: null,
  expiresAt: null,
  mode: null,
  isInitializing: true,
  isAuthenticating: false,
  isRefreshing: false,
  error: null,
  emitter: createAuthEmitter(),
  previousUserId: null,
  signOutFn: null,
  suspense: { promise: null, resolve: null },
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  setAuth: (
    user: FrontendUser,
    mode: AuthMode,
    token?: string | null,
    expiresAt?: number | null
  ) => {
    const state = get();
    const previousUserId = state.user?.id ?? null;
    const newUserId = user.id;

    // Resolve any pending suspense before updating state
    if (state.suspense.resolve) {
      state.suspense.resolve(user);
    }

    set({
      user,
      token: token ?? null,
      expiresAt: expiresAt ?? null,
      mode,
      isInitializing: false,
      isAuthenticating: false,
      isRefreshing: false,
      error: null,
      previousUserId,
      suspense: { promise: null, resolve: null },
    });

    if (previousUserId && previousUserId !== newUserId) {
      state.emitter.emit("user_changed", previousUserId, newUserId);
    }
  },

  clearAuth: (error?: AuthError | null) => {
    set({
      user: null,
      token: null,
      expiresAt: null,
      isInitializing: false,
      isAuthenticating: false,
      isRefreshing: false,
      error: error ?? null,
      previousUserId: null,
    });
  },

  setInitializing: (value: boolean) => {
    set({ isInitializing: value });
  },

  setAuthenticating: (value: boolean) => {
    set({ isAuthenticating: value });
  },

  setRefreshing: (value: boolean) => {
    set({ isRefreshing: value });
  },

  setError: (error: AuthError | null) => {
    set({ error });
  },

  emitSignedOut: () => {
    const { emitter } = get();
    emitter.emit("signed_out");
  },

  emitUserChanged: (previousId: string, newId: string) => {
    const { emitter } = get();
    emitter.emit("user_changed", previousId, newId);
  },

  emitTokenRefreshed: (token: string) => {
    const { emitter } = get();
    emitter.emit("token_refreshed", token);
  },

  reset: () => {
    set({
      ...initialState,
      emitter: get().emitter, // Keep the same emitter instance
    });
  },

  registerSignOut: (fn) => {
    set({ signOutFn: fn });
  },

  signOut: async () => {
    const { signOutFn } = get();
    if (!signOutFn) {
      throw new Error(
        "signOut: No auth strategy registered. " +
          "Ensure an auth provider (AuthCookieProvider or AuthPkceProvider) is mounted."
      );
    }
    await signOutFn();
  },

  getSuspensePromise: () => {
    const { suspense } = get();
    if (suspense.promise) {
      return suspense.promise;
    }

    const promise = new Promise<FrontendUser>((resolve) => {
      set({ suspense: { promise: null, resolve } });
    });

    set((state) => ({
      suspense: { ...state.suspense, promise },
    }));

    return promise;
  },

  resolveSuspense: (user: FrontendUser) => {
    const { suspense } = get();
    if (suspense.resolve) {
      suspense.resolve(user);
      set({ suspense: { promise: null, resolve: null } });
    }
  },
}));

/** Selector to derive UI status from auth state. */
export const selectStatus = (s: AuthStore): AuthUiStatus => {
  if (s.isInitializing) {
    return "initializing";
  }
  if (s.error && !s.user) {
    return "error";
  }
  if (s.user) {
    return "authenticated";
  }
  return "unauthenticated";
};

/** Get the current auth status as a discriminated value. */
export const useAuthStatus = (): AuthUiStatus => useAuthStore(selectStatus);

/** Subscribe to auth events. */
export const useAuthEvent = createUseAuthEvent(
  () => useAuthStore.getState().emitter
);
